import { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const TZ = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "America/Los_Angeles", "Australia/Sydney"];

export default function WorkingHoursPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hours, setHours] = useState({ start: "09:00", end: "21:00", tz: "Asia/Kolkata" });
  const [holidays, setHolidays] = useState([]);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/tenants/me");
      setTenant(r.data);
      setHours(r.data?.working_hours || { start: "09:00", end: "21:00", tz: "Asia/Kolkata" });
      setHolidays(r.data?.holidays || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!tenant) return;
    setSaving(true);
    try {
      await api.patch(`/tenants/${tenant.id}`, { working_hours: hours, holidays });
      toast.success("Saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  function addHoliday() { setHolidays((h) => [...h, { date: "", name: "" }]); }
  function updateHoliday(i, k, v) { setHolidays((h) => h.map((x, j) => j === i ? { ...x, [k]: v } : x)); }
  function removeHoliday(i) { setHolidays((h) => h.filter((_, j) => j !== i)); }

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="hours-title">Working Hours & Holidays</h1>
          <p className="text-sm text-muted-foreground mt-1">Calls outside hours route to voicemail / closed-message. Holidays override.</p>
        </div>
        {canWrite && <Button data-testid="hours-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Save className="w-4 h-4"/> Save</Button>}
      </div>
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-primary"/><h2 className="font-display font-bold">Open hours</h2></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Start</Label><Input data-testid="hours-start" type="time" value={hours.start||""} onChange={(e)=>setHours({...hours, start:e.target.value})} /></div>
          <div><Label>End</Label><Input data-testid="hours-end" type="time" value={hours.end||""} onChange={(e)=>setHours({...hours, end:e.target.value})} /></div>
          <div><Label>Timezone</Label>
            <Select value={hours.tz} onValueChange={(v)=>setHours({...hours, tz:v})}>
              <SelectTrigger data-testid="hours-tz" className="h-9"><SelectValue/></SelectTrigger>
              <SelectContent>{TZ.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>
      <Card className="p-5 mt-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold">Holidays ({holidays.length})</h2>
          {canWrite && <Button data-testid="add-holiday" onClick={addHoliday} variant="outline" className="gap-1.5"><Plus className="w-4 h-4"/> Add</Button>}
        </div>
        <div className="space-y-2">
          {holidays.map((h, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
              <Input data-testid={`holiday-date-${i}`} type="date" value={h.date||""} onChange={(e)=>updateHoliday(i, "date", e.target.value)} />
              <Input data-testid={`holiday-name-${i}`} placeholder="Holiday name" value={h.name||""} onChange={(e)=>updateHoliday(i, "name", e.target.value)} />
              {canDelete && <Button data-testid={`del-holiday-${i}`} onClick={()=>removeHoliday(i)} variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4"/></Button>}
            </div>
          ))}
          {holidays.length === 0 && <div className="text-sm text-muted-foreground py-3">No holidays configured.</div>}
        </div>
      </Card>
    </div>
  );
}
